import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  addDays, iso, parseIso, startOfMonth, endOfMonth, rangeDays,
  fmtFull, fmtShort, JOURS_LONG, MOIS, minutesFromHHMM, fmtMinutes, decimalHours,
} from "@/lib/date";
import { ChevronLeft, ChevronRight, Save, Lock, CheckCircle2, Filter } from "lucide-react";

export default function Heures() {
  const { user } = useAuth();
  const [month, setMonth] = useState(new Date());
  const [shifts, setShifts] = useState([]);
  const [hours, setHours] = useState({}); // dateIso -> {arr, dep, pause_minutes, validated}
  const [holidays, setHolidays] = useState(new Set());
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);
  const [shiftsOnly, setShiftsOnly] = useState(false);

  const first = startOfMonth(month);
  const last = endOfMonth(month);

  const load = async () => {
    const start = iso(first), end = iso(last);
    const [sh, hr, st, h] = await Promise.all([
      api.get("/shifts", { params: { start, end, student_id: user.id } }),
      api.get("/hours", { params: { start, end } }),
      api.get("/settings"),
      api.get(`/holidays/${month.getFullYear()}`),
    ]);
    setShifts(sh.data);
    const hm = {};
    hr.data.forEach((h) => {
      hm[h.date] = { arr: h.arr || "", dep: h.dep || "", pause_minutes: h.pause_min ?? 30, validated: !!h.validated };
    });
    setHours(hm);
    setSettings(st.data);
    setHolidays(new Set(h.data.map((x) => x.date)));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);

  const shiftMap = useMemo(() => {
    const m = {};
    shifts.forEach((s) => { m[s.date] = s; });
    return m;
  }, [shifts]);

  const days = rangeDays(first, last);

  // Filter only days with a shift OR encoded hours OR open weekday
  const visibleDays = days.filter((d) => {
    const isoD = iso(d);
    if (shiftsOnly) return !!shiftMap[isoD];
    return shiftMap[isoD] || hours[isoD] || (d.getDay() !== 0 && !holidays.has(isoD));
  });

  const shiftsCount = useMemo(
    () => days.filter((d) => shiftMap[iso(d)]).length,
    [days, shiftMap]
  );

  const updateDay = (dateIso, patch) => {
    setHours((prev) => ({
      ...prev,
      [dateIso]: { ...(prev[dateIso] || { arr: "", dep: "", pause_minutes: settings?.default_pause_minutes ?? 30, validated: false }), ...patch },
    }));
  };

  const prefillFromShift = (dateIso) => {
    const sh = shiftMap[dateIso];
    if (!sh) return;
    updateDay(dateIso, { arr: sh.start, dep: sh.end, pause_minutes: settings?.default_pause_minutes ?? 30 });
  };

  const saveDay = async (dateIso) => {
    const v = hours[dateIso];
    if (!v?.arr || !v?.dep) {
      toast.error("Indique au moins l'arrivée et le départ");
      return;
    }
    setBusy(true);
    try {
      await api.post("/hours", {
        date: dateIso,
        arr: v.arr,
        dep: v.dep,
        pause_minutes: Number(v.pause_minutes) || 0,
      });
      toast.success("Heures enregistrées");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  // Récap mensuel personnel
  const totalMin = useMemo(() => {
    return Object.values(hours).reduce((acc, h) => {
      const mins = Math.max(0, minutesFromHHMM(h.dep) - minutesFromHHMM(h.arr) - (Number(h.pause_minutes) || 0));
      return acc + mins;
    }, 0);
  }, [hours]);
  const daysWorked = Object.values(hours).filter((h) => h.arr && h.dep).length;

  return (
    <div className="space-y-6" data-testid="page-heures-student">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="label-tiny">Mes heures</p>
          <h1 className="font-display text-3xl sm:text-4xl text-[#1A2B27] mt-1">
            {MOIS[month.getMonth()]} {month.getFullYear()}
          </h1>
          <p className="text-[#6b7f79] mt-1">
            {daysWorked} jour{daysWorked > 1 ? "s" : ""} travaillé{daysWorked > 1 ? "s" : ""} · <span className="font-bold text-[#2D5951]">{fmtMinutes(totalMin)}</span> presté ({decimalHours(totalMin)} h)
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} data-testid="month-prev" className="rounded-full border-[#DDE7E4]"><ChevronLeft className="w-4 h-4" /></Button>
          <Button variant="outline" size="sm" onClick={() => setMonth(new Date())} data-testid="month-today" className="rounded-full border-[#DDE7E4] text-[#6b7f79]">Mois courant</Button>
          <Button variant="outline" size="icon" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} data-testid="month-next" className="rounded-full border-[#DDE7E4]"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 px-1">
        <div className="flex items-center gap-2.5 rounded-full bg-[#E8F0EE]/60 border border-[#C9DBD6] px-3.5 py-2">
          <Filter className="w-3.5 h-3.5 text-[#3E6E64]" />
          <Label htmlFor="filter-shifts-only" className="text-xs text-[#3D504B] font-medium cursor-pointer select-none">
            Avec shift uniquement
          </Label>
          <Switch
            id="filter-shifts-only"
            checked={shiftsOnly}
            onCheckedChange={setShiftsOnly}
            data-testid="heures-filter-shifts-only"
          />
        </div>
        <span className="text-xs text-[#94ADA7]" data-testid="heures-shifts-count">
          {shiftsCount} jour{shiftsCount > 1 ? "s" : ""} avec shift ce mois
        </span>
      </div>

      <div className="space-y-2">
        {visibleDays.length === 0 && shiftsOnly && (
          <Card className="card-soft border-0">
            <CardContent className="p-6 text-center text-[#94ADA7]" data-testid="heures-empty-state">
              Aucun shift planifié ce mois-ci. Décoche le filtre pour encoder une journée libre.
            </CardContent>
          </Card>
        )}
        {visibleDays.map((d) => {
          const isoD = iso(d);
          const v = hours[isoD];
          const sh = shiftMap[isoD];
          const isClosed = d.getDay() === 0 || holidays.has(isoD);
          if (isClosed && !v && !sh) return null;
          const validated = v?.validated;
          const mins = v ? Math.max(0, minutesFromHHMM(v.dep) - minutesFromHHMM(v.arr) - (Number(v.pause_minutes) || 0)) : 0;
          return (
            <Card key={isoD} className="card-soft border-0" data-testid={`heures-card-${isoD}`}>
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex items-center gap-3 sm:w-44 shrink-0">
                    <div className="text-center w-12">
                      <div className="text-[10px] uppercase tracking-wider text-[#94ADA7] font-semibold">
                        {JOURS_LONG[(d.getDay() + 6) % 7].slice(0, 3)}
                      </div>
                      <div className="font-display text-2xl text-[#1A2B27] leading-none">{d.getDate()}</div>
                    </div>
                    <div className="flex flex-col gap-1">
                      {sh && (
                        <Badge variant="outline" className="border-[#2D5951] text-[#2D5951] rounded-full text-[10px]">
                          Shift {sh.start}-{sh.end}
                        </Badge>
                      )}
                      {validated && (
                        <Badge className="bg-[#388E3C] text-white rounded-full text-[10px] hover:bg-[#388E3C]">
                          <Lock className="w-3 h-3 mr-1" /> Validé
                        </Badge>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-1 flex-wrap items-center gap-2">
                    <div className="flex flex-col">
                      <span className="label-tiny mb-0.5">Arrivée</span>
                      <Input
                        type="time" value={v?.arr || ""} disabled={validated}
                        onChange={(e) => updateDay(isoD, { arr: e.target.value })}
                        data-testid={`heures-arr-${isoD}`}
                        className="w-28 h-10 rounded-xl border-[#DDE7E4]"
                      />
                    </div>
                    <div className="flex flex-col">
                      <span className="label-tiny mb-0.5">Départ</span>
                      <Input
                        type="time" value={v?.dep || ""} disabled={validated}
                        onChange={(e) => updateDay(isoD, { dep: e.target.value })}
                        data-testid={`heures-dep-${isoD}`}
                        className="w-28 h-10 rounded-xl border-[#DDE7E4]"
                      />
                    </div>
                    <div className="flex flex-col">
                      <span className="label-tiny mb-0.5">Pause (min)</span>
                      <Input
                        type="number" min={0} max={240}
                        value={v?.pause_minutes ?? 30} disabled={validated}
                        onChange={(e) => updateDay(isoD, { pause_minutes: Number(e.target.value) || 0 })}
                        data-testid={`heures-pause-${isoD}`}
                        className="w-24 h-10 rounded-xl border-[#DDE7E4]"
                      />
                    </div>
                    <div className="flex flex-col">
                      <span className="label-tiny mb-0.5">Presté</span>
                      <div className="h-10 px-3 rounded-xl bg-[#E8F0EE] border border-[#C9DBD6] flex items-center font-bold text-[#3E6E64] min-w-[80px]" data-testid={`heures-total-${isoD}`}>
                        {fmtMinutes(mins)}
                      </div>
                    </div>
                    <div className="flex gap-2 ml-auto items-end">
                      {sh && (
                        <Button
                          variant="outline" size="sm"
                          onClick={() => prefillFromShift(isoD)}
                          disabled={validated}
                          data-testid={`heures-prefill-${isoD}`}
                          className="rounded-full border-[#DDE7E4] text-[#6b7f79] h-10"
                        >
                          Pré-remplir
                        </Button>
                      )}
                      <Button
                        size="sm"
                        onClick={() => saveDay(isoD)}
                        disabled={busy || validated}
                        data-testid={`heures-save-${isoD}`}
                        className="rounded-full bg-[#2D5951] hover:bg-[#1F423C] h-10"
                      >
                        <Save className="w-4 h-4 mr-1" /> Enregistrer
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
